import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Center, Spinner } from '@chakra-ui/react';
import { RequireAuth } from '../features/auth/RequireAuth';
import { AppLayout } from './AppLayout';
import { isCarouselCompleted } from '../services/carouselStorage';

/**
 * Redirects to the onboarding carousel until it has been completed (localStorage
 * flag). Sits *inside* RequireAuth so authentication is checked first.
 */
function OnboardingGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [carouselDone, setCarouselDone] = useState(false);

  useEffect(() => {
    setCarouselDone(isCarouselCompleted());
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="brand.500" thickness="3px" />
      </Center>
    );
  }

  if (!carouselDone && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * Layout route for the authenticated app: require an OIDC session, require the
 * onboarding carousel, then render the app shell around the matched page.
 */
export function ProtectedLayout() {
  return (
    <RequireAuth>
      <OnboardingGuard>
        <AppLayout>
          <Outlet />
        </AppLayout>
      </OnboardingGuard>
    </RequireAuth>
  );
}

export default ProtectedLayout;
